import React, { useEffect, useRef, useState } from "react";
import { apiGet } from "../../utils/apiClient";

// 商品分析ダッシュボード（RN-161）共有ローダー
// 小野さん提供のプロトタイプ HTML（public/product-analysis.html）を Shadow DOM にカプセル化して表示する。
// 実装方針は RN-153（目標管理ダッシュボード／TargetDashboardPage.tsx）と同一:
//  - CSS はプロトタイプのものをそのまま shadow root 内に閉じ込める（アプリ Tailwind と衝突しない／iframe 不要）。
//  - 描画 JS は document.getElementById / querySelectorAll だけを shadow root 参照へ差し替えて実行する。
//  - ヘッダーはプロトタイプ内蔵のもの1つに統一（アプリ側ヘッダーは付けない）。
//  - データは API（/api/product-analysis）から取得し window.__RN161_DATA__ / __RN161_CONFIG__ に注入。
//    取得できない場合はプロトタイプの埋め込みサンプルデータを描画せず、エラー表示にとどめる。
//
// プロトは「1画面5タブ」構成だが、本アプリでは左メニュー「分析をする」内の3メニュー
// （カテゴリー／商品名／商品分析）へ分割する。プロト HTML は描画 JS の冒頭フック以外は無編集とし、
// マウント後に Shadow DOM 上でタブの出し分けだけを行う（views / defaultView）。
//  - views が1つ → タブバー（#tabs）ごと非表示にし、その1ビューのみ表示。
//  - views が複数 → 対象外のタブボタンを隠し、defaultView を活性化（プロト標準のタブ切替JSをそのまま流用）。
//
// 限界利益（限界利益率）・前月実データ・経過日数・サムネ画像・広告指標は実データ。計算（要因分解WF・
// 着地見込み・ROAS 等）はプロト JS がそのまま行う。

const PROTOTYPE_URL = "/product-analysis.html";

// API のプロト globals 形状の契約。null は API 未接続（エラー表示）。
interface ProductAnalysisContract {
    config?: { storeName?: string; [k: string]: any };
    prods?: any[];
    cats?: any[];
    kws?: any[];
}

// アプリ埋め込み時のUI微調整。プロトHTMLは無編集のままとし、Shadow DOM内でのみCSSを上書きする。
//  - 上部トグル2行（表示／基準月）の縦ずれ（基準月のインラインmargin-top:8px）を解消する
//  - セグメント・ミニセグ・入力・セレクト・KW追加ボタンの高さをそろえて操作行を整える
//  - 広い画面でコンテンツが間延びしないよう最大幅を抑える
const OVERRIDE_CSS = `
.head .ctrl{align-items:center}
.head .ctrl-row{margin-top:0 !important}
.seg button,.miniseg button{display:inline-flex;align-items:center;padding-top:0;padding-bottom:0}
.seg button{height:32px}
.miniseg button{height:32px;font-size:12px}
.ctrl-input,.ctrl-sel,.kw-input input{height:34px}
.kw-input button{height:34px;display:inline-flex;align-items:center}
.tab{padding:9px 16px}
.wrap{max-width:1360px}
`;

interface ProductAnalysisShadowProps {
    // プロト内部のビューキー: "flow" | "cat" | "prod" | "kw" | "rank"
    views: string[];
    defaultView: string;
}

export default function ProductAnalysisShadow({ views, defaultView }: ProductAnalysisShadowProps) {
    const hostRef = useRef<HTMLDivElement | null>(null);
    const mountedRef = useRef(false);
    const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");

    useEffect(() => {
        if (mountedRef.current) return; // StrictMode の二重実行・再マウント対策
        const host = hostRef.current;
        if (!host || host.shadowRoot) return;
        mountedRef.current = true;

        (async () => {
            try {
                // 1) API 契約（取れなければ null＝データ取得失敗）とプロトタイプ HTML を並列取得
                const [contract, html] = await Promise.all([
                    apiGet(`/api/product-analysis`)
                        .then(async (res) => {
                            const ct = res.headers.get("content-type") || "";
                            if (res.ok && ct.includes("application/json")) {
                                const j = (await res.json()) as ProductAnalysisContract;
                                if (j && Array.isArray(j.prods) && j.prods.length) return j;
                            }
                            return null;
                        })
                        .catch(() => null),
                    fetch(PROTOTYPE_URL, { cache: "no-store" }).then((r) => r.text()),
                ]);

                // API からデータが取れない場合は、プロト埋め込みのサンプルデータを表示せず
                // エラー表示にとどめる（実データが無いのにダッシュボードが動いているように
                // 見えるのを防ぐ）
                if (!contract) {
                    setStatus("error");
                    return;
                }

                // 店舗表示名：API は shop_url を返すので、ブラウザの会社情報（localStorage）から
                // 一致する表示名（companyName / shopName）があれば config.storeName を差し替える
                if (contract.config) {
                    try {
                        const companies = JSON.parse(localStorage.getItem("companies") || "[]");
                        const su = String(contract.config.storeName || "");
                        const c = Array.isArray(companies)
                            ? companies.find((x: any) => String(x?.shopUrl || "") === su)
                            : null;
                        const name = c && (c.companyName || c.shopName);
                        if (name) contract.config.storeName = name;
                    } catch {
                        /* 名前が引けなければ shop_url のまま */
                    }
                }
                // プロト globals へ注入（プロト冒頭が window.__RN161_* を優先して読む）
                (window as any).__RN161_CONFIG__ = contract.config || undefined;
                (window as any).__RN161_DATA__ = {
                    prods: contract.prods || [],
                    cats: contract.cats || [],
                    kws: contract.kws || [],
                };

                // 2) プロトタイプ HTML から CSS / body / script を抽出
                const doc = new DOMParser().parseFromString(html, "text/html");
                const css = Array.from(doc.querySelectorAll("style"))
                    .map((s) => s.textContent || "")
                    .join("\n");
                const scripts = Array.from(doc.querySelectorAll("script"))
                    .map((s) => s.textContent || "")
                    .join("\n;\n");
                doc.querySelectorAll("script").forEach((s) => s.remove());
                const bodyHtml = doc.body ? doc.body.innerHTML : "";

                // 2) shadow root に CSS + markup を投入
                //    プロトの色テーマは :root のCSS変数。Shadow DOM 内では :root が <html> を指し
                //    一致しないため :host に置換し、shadow ホストに変数を載せて内部へ継承させる。
                const shadowCss = css.split(":root").join(":host");
                const shadow = host.attachShadow({ mode: "open" });
                shadow.innerHTML = `<style>${shadowCss}</style><style>${OVERRIDE_CSS}</style>${bodyHtml}`;

                // 3) 描画 JS を shadow root 参照に差し替えて実行
                const transformed = scripts
                    .split("document.getElementById").join("__root.getElementById")
                    .split("document.querySelectorAll").join("__root.querySelectorAll");
                // eslint-disable-next-line no-new-func
                const run = new Function("__root", transformed);
                run(shadow);

                // 4) メニュー分割に合わせてタブを出し分ける（プロト HTML は無編集）
                applyViewSelection(shadow, views, defaultView);

                setStatus("ready");
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error("[ProductAnalysis] mount failed", e);
                setStatus("error");
            }
        })();
        // views/defaultView はマウント時固定（ページごとに別コンポーネント）なので依存に含めない
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="w-full">
            {status === "loading" && <div className="text-sm text-slate-400 mb-2">読み込み中…</div>}
            {status === "error" && (
                <div className="mx-auto mt-6 max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center">
                    <div className="mb-1 text-sm font-semibold text-slate-700">
                        商品分析データを表示できませんでした
                    </div>
                    <div className="mb-4 text-[13px] leading-relaxed text-slate-500">
                        対象期間のデータがまだ無いか、一時的にデータを取得できませんでした。時間をおいて再度お試しください。
                    </div>
                    <button
                        type="button"
                        onClick={() => window.location.reload()}
                        className="rounded-md bg-slate-700 px-4 py-2 text-[13px] font-semibold text-white transition hover:bg-slate-800"
                    >
                        再読み込み
                    </button>
                </div>
            )}
            {/* プロトタイプは Shadow DOM 内に閉じる（CSS はアプリへ漏れない） */}
            {status !== "error" && <div ref={hostRef} />}
        </div>
    );
}

// Shadow DOM 上でタブの表示/活性を制御する。プロト標準の #tabs クリック切替JSはそのまま動く。
function applyViewSelection(root: ShadowRoot, views: string[], defaultView: string) {
    const tabBar = root.getElementById("tabs");
    const tabs = Array.from(root.querySelectorAll<HTMLElement>("#tabs .tab"));

    // 対象外のタブボタンを隠す
    tabs.forEach((t) => {
        const v = t.getAttribute("data-v") || "";
        if (!views.includes(v)) t.style.display = "none";
    });

    // defaultView を活性化（.view と .tab の .on を付け替え）
    root.querySelectorAll(".view").forEach((v) => v.classList.remove("on"));
    const target = root.getElementById("v-" + defaultView);
    if (target) target.classList.add("on");
    tabs.forEach((t) => t.classList.toggle("on", t.getAttribute("data-v") === defaultView));

    // 単一ビューならタブバーごと隠す
    if (views.length <= 1 && tabBar) tabBar.style.display = "none";
}
